export const navigationLinks = [
  { label: "Services", href: "#services" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Pricing", href: "#pricing" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
];

export const heroData = {
  badge: "Professional Python Code Audits",
  headline: "Ship Better Python Code,",
  headlineAccent: "With Confidence.",
  subheadline:
    "Expert code reviews and audits that eliminate technical debt, improve architecture, and help your team ship production-ready code faster.",
  primaryCTA: { label: "View Plans", href: "#pricing" },
  secondaryCTA: { label: "How It Works", href: "#how-it-works" },
  codeLines: [
    { text: '# Before audit', type: 'comment' },
    { text: 'def process(data):', type: 'keyword' },
    { text: '    result = []', type: 'default' },
    { text: '    for item in data:', type: 'keyword' },
    { text: "        if item['status'] == 'active':", type: 'string' },
    { text: '            result.append(item)', type: 'default' },
    { text: '    return result', type: 'return' },
    { text: '', type: 'default' },
    { text: '# After audit', type: 'success' },
    { text: 'def filter_active(', type: 'keyword' },
    { text: '    items: list[dict],', type: 'type' },
    { text: ') -> list[dict]:', type: 'type' },
    { text: '    """Return only active items."""', type: 'string' },
    { text: '    return [', type: 'return' },
    { text: '        item for item in items', type: 'default' },
    { text: '        if item.get("status") == "active"', type: 'string' },
    { text: '    ]', type: 'return' },
  ],
};

export const problemsData = [
  {
    title: "Technical Debt",
    description:
      "Accumulated shortcuts and quick fixes that slow down every future change and erode confidence.",
    iconName: "AlertTriangle",
  },
  {
    title: "Poor Scalability",
    description:
      "Code that works today but buckles under production load or when the team grows.",
    iconName: "TrendingDown",
  },
  {
    title: "Fragile Architecture",
    description:
      "Tightly coupled modules that make changes risky and every deploy nerve-wracking.",
    iconName: "Unlink",
  },
  {
    title: "Slow Releases",
    description:
      "Unclear patterns and inconsistent code that slows down every sprint cycle.",
    iconName: "Timer",
  },
  {
    title: "Hidden Bugs",
    description:
      "Subtle issues lurking in untested edge cases and overlooked code paths.",
    iconName: "Bug",
  },
  {
    title: "Knowledge Gaps",
    description:
      "Missing documentation and tribal knowledge that creates team bottlenecks.",
    iconName: "CircleHelp",
  },
];

export const solutionsData = [
  {
    title: "Risk Reduction",
    description:
      "Identify and prioritize issues before they become costly production incidents.",
    iconName: "ShieldCheck",
  },
  {
    title: "Faster Shipping",
    description:
      "Clean architecture and clear patterns mean your team moves faster, not slower.",
    iconName: "Zap",
  },
  {
    title: "Clearer Architecture",
    description:
      "Actionable recommendations for structure, patterns, and separation of concerns.",
    iconName: "GitBranch",
  },
];

export const servicesData = [
  {
    title: "Python Code Review",
    description:
      "Line-by-line review of your Python code with actionable feedback on readability, correctness, and idiomatic patterns.",
    iconName: "FileSearch",
    features: [
      "Readability assessment",
      "Bug detection",
      "Style consistency",
      "Idiomatic Python suggestions",
    ],
  },
  {
    title: "Code Audit & Refactoring Guidance",
    description:
      "Comprehensive assessment of code health with a prioritized action plan for improvements.",
    iconName: "ClipboardCheck",
    features: [
      "Technical debt analysis",
      "Refactoring roadmap",
      "Complexity metrics",
      "Priority-ranked findings",
    ],
  },
  {
    title: "Design Patterns & Architecture",
    description:
      "Expert recommendations on structuring your codebase for clarity, extensibility, and maintainability.",
    iconName: "Layers",
    features: [
      "Pattern recommendations",
      "Module structure",
      "Dependency management",
      "Interface design",
    ],
  },
  {
    title: "Scalability & Maintainability",
    description:
      "Ensure your code and architecture are ready to grow with your product and team.",
    iconName: "Maximize2",
    features: [
      "Performance hotspots",
      "Scaling bottlenecks",
      "Testability review",
      "Future-proofing advice",
    ],
  },
  {
    title: "Security & Dependency Hygiene",
    description:
      "Review of dependency health, common security pitfalls, and safe coding practices.",
    iconName: "Lock",
    features: [
      "Dependency audit",
      "Known vulnerabilities",
      "Input validation",
      "Safe defaults review",
    ],
  },
];

export const processSteps = [
  {
    step: 1,
    title: "Share Your Code",
    description:
      "Provide a code snippet, file, or repository access along with context about your goals and concerns.",
    details: "GitHub/GitLab links, zip files, or direct code snippets.",
    iconName: "Code",
  },
  {
    step: 2,
    title: "Expert Review",
    description:
      "Our Python expert conducts a thorough review focusing on your specific needs and priorities.",
    details:
      "Readability, architecture, performance, patterns, and more.",
    iconName: "Search",
  },
  {
    step: 3,
    title: "Receive Your Report",
    description:
      "Get a detailed, annotated report with prioritized findings and an actionable improvement plan.",
    details:
      "Written report + optional video walkthrough depending on your plan.",
    iconName: "FileText",
  },
  {
    step: 4,
    title: "Improve & Ship",
    description:
      "Implement the recommendations at your own pace with clear guidance and priority rankings.",
    details: "Follow-up questions welcome within the scope of your plan.",
    iconName: "Rocket",
  },
];

export const pricingPlans = [
  {
    id: "silver-3m",
    name: "Silver",
    period: "3M",
    price: 19,
    currency: "EUR",
    lineLimit: 150,
    description: "Quick review for small modules or utilities",
    features: [
      "Up to 150 lines of Python code",
      "Written review with annotations",
      "Prioritized issue list",
      "Actionable recommendations",
      "Email follow-up questions",
    ],
    popular: false,
    highlight: false,
  },
  {
    id: "gold-6m",
    name: "Gold",
    period: "6M",
    price: 34,
    currency: "EUR",
    lineLimit: 350,
    description: "Thorough review for features or services",
    features: [
      "Up to 350 lines of Python code",
      "Written review with annotations",
      "Prioritized issue list",
      "Architecture recommendations",
      "Design pattern suggestions",
      "Email follow-up questions",
    ],
    popular: false,
    highlight: false,
  },
  {
    id: "platinum-12m",
    name: "Platinum",
    period: "12M",
    price: 55,
    currency: "EUR",
    lineLimit: 800,
    description: "In-depth audit for larger codebases",
    features: [
      "Up to 800 lines of Python code",
      "Comprehensive written report",
      "Architecture & scalability analysis",
      "Refactoring roadmap",
      "Performance recommendations",
      "Design pattern guidance",
      "Email follow-up questions",
    ],
    popular: true,
    highlight: false,
  },
  {
    id: "diamond-12m",
    name: "Diamond",
    period: "12M",
    price: 69,
    currency: "EUR",
    lineLimit: 1000,
    description: "Premium audit with video walkthrough",
    features: [
      "Up to 1000 lines of Python code",
      "Comprehensive written report",
      "Detailed video review walkthrough",
      "Architecture & scalability analysis",
      "Refactoring roadmap",
      "Performance & security review",
      "Priority support & follow-up",
    ],
    popular: false,
    highlight: true,
  },
];

export const testimonials = [
  {
    name: "Sarah K.",
    role: "CTO",
    company: "SaaS Startup",
    quote:
      "The code audit helped us identify critical scalability issues before our product launch. The recommendations were clear, prioritized, and immediately actionable.",
    rating: 5,
  },
  {
    name: "Marcus L.",
    role: "Lead Developer",
    company: "FinTech Company",
    quote:
      "We got more value from one audit than months of internal code reviews. The architectural suggestions transformed how we structure our Python services.",
    rating: 5,
  },
  {
    name: "Emily R.",
    role: "Engineering Manager",
    company: "E-commerce Platform",
    quote:
      "Professional, thorough, and genuinely helpful. The video walkthrough made it easy for the whole team to understand and implement the changes.",
    rating: 5,
  },
];

export const faqItems = [
  {
    question: "What exactly do you review in a code audit?",
    answer:
      "We review your Python code for readability, architecture quality, performance hotspots, testability, maintainability, and adherence to idiomatic Python patterns. Each review is tailored to your specific goals and context.",
  },
  {
    question: "How do you handle confidentiality?",
    answer:
      "Your code is treated with strict confidentiality. We do not share, store long-term, or reuse any code submitted for review. We're happy to sign an NDA if required.",
  },
  {
    question: "What do you need from me to get started?",
    answer:
      "We need your code (snippet, file, or repo access), context about what the code does, and any specific goals or concerns you have. The more context you provide, the more targeted our review will be.",
  },
  {
    question: "What do the deliverables look like?",
    answer:
      "You'll receive a detailed written report with annotated findings, prioritized by severity and impact. Each finding includes an explanation, recommendation, and code examples where helpful. The Diamond plan also includes a video walkthrough.",
  },
  {
    question: "Can you review code from private repositories?",
    answer:
      "Yes. We can work with private GitHub or GitLab repositories. You can grant temporary read access, and we'll guide you through the setup if needed.",
  },
  {
    question: "Do you offer follow-up support after the review?",
    answer:
      "Yes. All plans include follow-up questions via email within the scope of the review. For more extensive follow-up or implementation guidance, we recommend the Diamond plan or a custom engagement.",
  },
  {
    question: "What is your refund policy?",
    answer:
      "If we're unable to deliver the agreed-upon review, you'll receive a full refund. For detailed information on cancellations and refunds, please see our Refund Policy and Cancellation Policy pages linked in the footer.",
  },
  {
    question: "How quickly will I receive my review?",
    answer:
      "Turnaround time depends on the plan and current workload. We'll provide an estimated delivery timeframe after receiving your code and reviewing the scope.",
  },
];

export const footerData = {
  brand: "PyAuditSystems",
  tagline:
    "Professional Python code audits and reviews for teams that ship quality software.",
  contact: {
    email: "support@pyauditsystems.com",
    address: "30 N Gould St, STE R, Sheridan, WY 82801, USA",
  },
  sections: [
    {
      title: "Services",
      links: [
        { label: "Code Review", href: "/#services" },
        { label: "Code Audit", href: "/#services" },
        { label: "Architecture Consulting", href: "/#services" },
        { label: "Custom Quote", href: "/custom-quote" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "How It Works", href: "/#how-it-works" },
        { label: "Pricing", href: "/#pricing" },
        { label: "FAQ", href: "/#faq" },
        { label: "Contact", href: "mailto:support@pyauditsystems.com" },
      ],
    },
    {
      title: "Legal",
      links: [
        { label: "Privacy Policy", href: "/privacy-policy" },
        { label: "Terms of Service", href: "/terms-of-service" },
        { label: "Refund Policy", href: "/refund-policy" },
        { label: "Cancellation Policy", href: "/cancellation-policy" },
        { label: "Delivery Policy", href: "/delivery-policy" },
        { label: "Legal Notice", href: "/legal-notice" },
        { label: "Cookie Policy", href: "/cookie-policy" },
      ],
    },
  ],
};

export const policyContent = {
  "privacy-policy": {
    title: "Privacy Policy",
    lastUpdated: "August 2025",
    sections: [
      {
        heading: "Introduction",
        content:
          "PyAuditSystems (\"we\", \"our\", \"us\") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our code review and audit services.",
      },
      {
        heading: "Information We Collect",
        content:
          "We collect information you provide directly: your email address, name, and any code or repository access you share for review purposes. We may also collect usage data such as browser type and IP address through cookies and analytics.",
      },
      {
        heading: "How We Use Your Information",
        content:
          "We use your information to deliver our code review services, communicate with you about your review, process payments, and improve our service quality. We do not sell your information to third parties.",
      },
      {
        heading: "Code Confidentiality",
        content:
          "All code submitted for review is treated with strict confidentiality. We do not share, publish, or reuse your code. Code is deleted after the review is completed and any follow-up period has elapsed, unless otherwise agreed.",
      },
      {
        heading: "Data Retention",
        content:
          "We retain your personal data for as long as necessary to provide our services and comply with legal obligations. Code submitted for review is retained only for the duration of the engagement plus a reasonable follow-up period.",
      },
      {
        heading: "Your Rights",
        content:
          "You have the right to access, correct, or delete your personal data. You may also request data portability or object to processing. Contact us at support@pyauditsystems.com to exercise your rights.",
      },
      {
        heading: "Contact",
        content:
          "For privacy-related inquiries, please contact us at support@pyauditsystems.com.",
      },
    ],
  },
  "terms-of-service": {
    title: "Terms of Service",
    lastUpdated: "August 2025",
    sections: [
      {
        heading: "Acceptance of Terms",
        content:
          "By accessing or using PyAuditSystems services, you agree to be bound by these Terms of Service. If you do not agree, please do not use our services.",
      },
      {
        heading: "Services Description",
        content:
          "PyAuditSystems provides professional Python code review and audit services. We deliver written reports, annotated feedback, and (where applicable) video walkthroughs based on the plan you select.",
      },
      {
        heading: "User Obligations",
        content:
          "You agree to provide accurate information, have the right to share any code submitted for review, and use our services in compliance with applicable laws. You are responsible for maintaining the confidentiality of your account.",
      },
      {
        heading: "Intellectual Property",
        content:
          "You retain all rights to your code. Our review reports and recommendations are provided for your use in improving your codebase. You may not redistribute our reports as a standalone product.",
      },
      {
        heading: "Limitation of Liability",
        content:
          "Our reviews are advisory in nature. We provide recommendations based on our expertise but do not guarantee specific outcomes. PyAuditSystems is not liable for any direct or indirect damages arising from the implementation of our recommendations.",
      },
      {
        heading: "Governing Law",
        content:
          "These terms are governed by the laws of the jurisdiction in which PyAuditSystems operates. Any disputes will be resolved through the appropriate legal channels in that jurisdiction.",
      },
    ],
  },
  "refund-policy": {
    title: "Refund Policy",
    lastUpdated: "August 2025",
    sections: [
      {
        heading: "Refund Eligibility",
        content:
          "We stand behind the quality of our service. If we are unable to deliver the agreed-upon code review within the communicated timeframe, you are eligible for a full refund.",
      },
      {
        heading: "Before Review Delivery",
        content:
          "If you request a cancellation before we begin the review process, you will receive a full refund. If the review is in progress, a partial refund may be offered based on work completed.",
      },
      {
        heading: "After Review Delivery",
        content:
          "Once the review report has been delivered, refunds are evaluated on a case-by-case basis. If you believe the review did not meet the described scope, please contact us within 7 days of delivery.",
      },
      {
        heading: "How to Request a Refund",
        content:
          "To request a refund, email support@pyauditsystems.com with your order details and reason for the request. We aim to process all refund requests within 5 business days.",
      },
    ],
  },
  "cancellation-policy": {
    title: "Cancellation Policy",
    lastUpdated: "August 2025",
    sections: [
      {
        heading: "Cancellation Before Review Starts",
        content:
          "You may cancel your review request at any time before we begin the review process for a full refund.",
      },
      {
        heading: "Cancellation During Review",
        content:
          "If you need to cancel after the review has begun, please contact us as soon as possible. A partial refund may be offered based on the amount of work already completed.",
      },
      {
        heading: "How to Cancel",
        content:
          "To cancel a review request, email support@pyauditsystems.com with your order details. We will confirm the cancellation and any applicable refund within 2 business days.",
      },
    ],
  },
  "delivery-policy": {
    title: "Delivery & Fulfillment Policy",
    lastUpdated: "August 2025",
    sections: [
      {
        heading: "Service Delivery",
        content:
          "PyAuditSystems is a digital service. All deliverables (written reports, annotated feedback, video walkthroughs) are delivered electronically via email or a secure link.",
      },
      {
        heading: "Delivery Timeline",
        content:
          "After receiving your code and confirming the scope, we will provide an estimated delivery timeframe. Turnaround varies by plan complexity and current workload.",
      },
      {
        heading: "What You Receive",
        content:
          "Depending on your plan, you will receive: a written review report with annotated findings, prioritized issue list, actionable recommendations, and (for Diamond plans) a detailed video walkthrough.",
      },
      {
        heading: "Delays",
        content:
          "If we anticipate a delay beyond the estimated timeframe, we will notify you promptly and provide an updated delivery estimate. You may choose to wait or request a cancellation.",
      },
    ],
  },
  "legal-notice": {
    title: "Legal Notice",
    lastUpdated: "August 2025",
    sections: [
      {
        heading: "Company Information",
        content:
          "PyAuditSystems is operated by [Company Name Placeholder], registered at [Business Address Placeholder]. Registration number: [Placeholder]. VAT ID: [Placeholder if applicable].",
      },
      {
        heading: "Contact",
        content:
          "Email: support@pyauditsystems.com. For legal inquiries, please use the email above or contact us by mail at the registered address.",
      },
      {
        heading: "Responsible for Content",
        content:
          "The content of this website is provided for informational purposes. While we strive for accuracy, we do not guarantee the completeness or timeliness of the information presented.",
      },
    ],
  },
  "cookie-policy": {
    title: "Cookie Policy",
    lastUpdated: "August 2025",
    sections: [
      {
        heading: "What Are Cookies",
        content:
          "Cookies are small text files stored on your device when you visit our website. They help us understand how you use our site and improve your experience.",
      },
      {
        heading: "Cookies We Use",
        content:
          "We use essential cookies for site functionality and analytics cookies to understand visitor behavior. We do not use advertising or tracking cookies from third parties.",
      },
      {
        heading: "Managing Cookies",
        content:
          "You can control cookies through your browser settings. Disabling essential cookies may affect site functionality. Analytics cookies can be opted out of without impact on service delivery.",
      },
    ],
  },
};
